import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Dropdown } from "../ui/dropdown/Dropdown"
import { DropdownItem } from "../ui/dropdown/DropdownItem"
import { MoreDotIcon } from "../../icons"
import { hasCurrentPolicy, type PermissionKey } from "../../lib/auth"

// Coordina que solo un RowActionsMenu esté abierto a la vez: cada instancia
// anuncia su apertura y las demás se cierran al escucharla.
const ROW_ACTIONS_OPEN_EVENT = "row-actions-menu:open"
let nextMenuId = 0

export type RowAction = {
  label: string
  icon: ReactNode
  onClick?: () => void
  to?: string
  variant?: "default" | "danger"
  disabled?: boolean
  /** Policy key(s) required to see this action — e.g. the `:edit` policy for the page's `:view` route. Omit for read-only navigation actions. */
  requires?: PermissionKey | PermissionKey[]
}

export type RowActionsMenuProps = {
  actions: RowAction[]
  /** Accessible label for the trigger button. Defaults to "Acciones". */
  label?: string
}

/**
 * Actions column for data tables: a single "..." trigger opens a dropdown of
 * icon+label options, instead of a row of bare text/icon buttons. Used
 * consistently across every admin data table in gestion.
 */
export function RowActionsMenu({ actions, label = "Acciones" }: RowActionsMenuProps) {
  const visibleActions = actions.filter((action) => {
    if (!action.requires) return true
    const keys = Array.isArray(action.requires) ? action.requires : [action.requires]
    return hasCurrentPolicy(...keys)
  })
  const [isOpen, setIsOpen] = useState(false)
  const menuId = useRef(++nextMenuId).current
  const close = () => setIsOpen(false)

  useEffect(() => {
    const handleOtherOpen = (e: Event) => {
      const openedId = (e as CustomEvent<number>).detail
      if (openedId !== menuId) setIsOpen(false)
    }
    window.addEventListener(ROW_ACTIONS_OPEN_EVENT, handleOtherOpen)
    return () => window.removeEventListener(ROW_ACTIONS_OPEN_EVENT, handleOtherOpen)
  }, [menuId])

  if (visibleActions.length === 0) return null

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen((v) => {
            const next = !v
            if (next) window.dispatchEvent(new CustomEvent(ROW_ACTIONS_OPEN_EVENT, { detail: menuId }))
            return next
          })
        }}
        className="dropdown-toggle flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-gray-300"
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <MoreDotIcon className="size-5" />
      </button>
      <Dropdown isOpen={isOpen} onClose={close} className="w-52 p-1.5" usePortal>
        {visibleActions.map((action, i) => (
          <DropdownItem
            key={i}
            tag={action.to ? "a" : "button"}
            to={action.to}
            onClick={action.disabled ? undefined : action.onClick}
            onItemClick={close}
            baseClassName="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors"
            className={
              action.disabled
                ? "cursor-not-allowed text-gray-300 dark:text-gray-600"
                : action.variant === "danger"
                ? "text-error-600 hover:bg-error-50 dark:text-error-400 dark:hover:bg-error-900/10"
                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
            }
          >
            <span className="shrink-0 [&>svg]:size-4">{action.icon}</span>
            <span>{action.label}</span>
          </DropdownItem>
        ))}
      </Dropdown>
    </div>
  )
}

export default RowActionsMenu
